import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  environmentReportSchema,
  SETUP_STEP_IDS,
  setupEventSchema,
  setupReportSchema,
  type EnvironmentCheck,
  type EnvironmentReport,
  type SetupReport,
  type SetupStep,
  type SetupStepId,
  type ToolboxAccessResponse,
  type ToolboxBridge,
  type ToolboxCapability,
} from "@ttc-spectree/toolbox-contract";
import { toolboxKeys } from "@/lib/toolbox/bridge";

/**
 * The ordered Setup list on SpecTree's side (epic PI-SPEC-SW-E288, feature
 * PI-SPEC-SW-1466; frame 1b, 03-evaluation.md section 8.6 Q10).
 *
 * An app with `getSetup` sends the five steps itself (`setupReportSchema`).
 * An older app has only `checkEnvironment()`; {@link mapEnvironmentToSteps}
 * maps its checks onto the same five rows, with no install buttons. Git is a
 * step such an app cannot report, so it is shown as not checked and never
 * blocks: Continue opens when the older app's own checks pass, as it did
 * before the ordered list. Every bridge payload is parsed with the contract
 * schemas; one that does not parse is ignored.
 */

/** One row of the page's list: a contract step, plus a flag for a step an older app cannot do. */
export type SetupRow = SetupStep & {
  /** True when this app is too old to report or finish the step (fallback only). */
  needsAppUpdate?: boolean;
};

export interface SetupRows {
  steps: SetupRow[];
}

/** Where the page's five rows came from. */
export type SetupSource = "setup" | "environment";

/** The step titles, in order (frame 1b). */
export const SETUP_STEP_TITLES: Readonly<Record<SetupStepId, string>> = {
  spectree: "SpecTree",
  "azure-devops": "Azure DevOps",
  "claude-code": "Claude Code",
  git: "Git",
  "project-code": "Project code",
};

/** The fallback's sentence for a step an older app cannot do. */
export const UPDATE_APP_FOR_STEP = "Update SpecTree Toolbox to finish this step.";

/** The fallback's Git line: an older app does not check Git, and it never blocks Continue. */
export const GIT_NOT_CHECKED = "This version of SpecTree Toolbox doesn't check Git.";

/** The page re-reads the report this often while it is open (frame 1d note: "checks again every minute"). */
export const SETUP_POLL_MS = 60_000;

export const setupKeys = {
  report: () => [...toolboxKeys.all, "setup"] as const,
};

/** The 1-based number of a step in the list. */
export function stepNumber(id: SetupStepId): number {
  return SETUP_STEP_IDS.indexOf(id) + 1;
}

function failed(check: EnvironmentCheck | undefined): boolean {
  return check?.status === "fail";
}

/**
 * Map an older app's checks onto the five Setup rows. Pure; exported for tests.
 *
 * - SpecTree: `spectree-account` (and `spectree-contract`, which only an app update fixes);
 * - Azure DevOps: `ado-pat` from either toolset (the page replaces this row with what SpecTree knows);
 * - Claude Code: `claude-installed`, then `claude-signed-in` (sign-in is offered, an install is not);
 * - Git: an older app cannot tell, so the row passes with "not checked" (an
 *   older app's checks never included Git, so it never blocked Continue);
 * - Project code: `grooming-tools` or `formulator-tools`, whichever passes
 *   (before the ordered list, one ready toolset was enough).
 *
 * The apps' own fix texts are not shown: older ones are commands (UX-PM-40).
 */
export function mapEnvironmentToSteps(report: EnvironmentReport): SetupRows {
  const { shared, toolsets } = report;
  const adoPat = toolsets.grooming?.["ado-pat"] ?? toolsets.formulator?.["ado-pat"];
  const toolsReady =
    toolsets.grooming?.["grooming-tools"].status === "ok" || toolsets.formulator?.["formulator-tools"].status === "ok";

  const spectree: SetupRow = failed(shared["spectree-contract"])
    ? { id: "spectree", status: "failed", detail: UPDATE_APP_FOR_STEP, needsAppUpdate: true }
    : failed(shared["spectree-account"])
      ? { id: "spectree", status: "todo", detail: "Sign in to SpecTree in this window." }
      : { id: "spectree", status: "ok" };

  const azureDevOps: SetupRow = adoPat?.status === "ok" ? { id: "azure-devops", status: "ok" } : { id: "azure-devops", status: "todo" };

  const claudeCode: SetupRow = failed(shared["claude-installed"])
    ? {
        id: "claude-code",
        status: "todo",
        detail: "Claude Code isn't on this computer yet. Update SpecTree Toolbox so it can install it for you.",
        needsAppUpdate: true,
      }
    : failed(shared["claude-signed-in"])
      ? {
          id: "claude-code",
          status: "todo",
          detail: "Claude Code is not signed in yet.",
          action: "sign-in-to-claude",
          actionLabel: "Sign in to Claude",
        }
      : { id: "claude-code", status: "ok", detail: "Found Claude Code · signed in" };

  const git: SetupRow = { id: "git", status: "ok", detail: GIT_NOT_CHECKED };

  const projectCode: SetupRow =
    toolsReady
      ? { id: "project-code", status: "ok" }
      : { id: "project-code", status: "todo", detail: UPDATE_APP_FOR_STEP, needsAppUpdate: true };

  return { steps: [spectree, azureDevOps, claudeCode, git, projectCode] };
}

/** The capabilities that run Claude Code on the person's own computer, against the project's code. */
export const COMPUTER_CAPABILITIES: readonly ToolboxCapability[] = ["prepare-sessions", "groom-one", "formulator", "map-project"];

/** The steps every person needs: their SpecTree account and their Azure DevOps connection. */
export const ACCOUNT_STEP_IDS: readonly SetupStepId[] = ["spectree", "azure-devops"];

/**
 * True when the person needs the computer steps too (Claude Code, Git,
 * Project code): an Admin, or a role on any project that runs Claude Code.
 * A Viewer only reads, so their Setup is SpecTree and Azure DevOps (PKG-PM-E
 * walk E-W6). True while access is unknown and for someone with no role yet,
 * so nobody is told less than they may need.
 */
export function needsComputerSteps(access: ToolboxAccessResponse | null | undefined): boolean {
  if (!access || access.isAdmin) return true;
  const withRoles = access.projects.filter((entry) => entry.roles.length > 0);
  if (withRoles.length === 0) return true;
  return withRoles.some((entry) => entry.capabilities.some((capability) => COMPUTER_CAPABILITIES.includes(capability)));
}

/** Only the account steps (SpecTree, Azure DevOps), for someone who needs no computer steps. */
export function accountStepsOnly(rows: SetupRows): SetupRows {
  return { steps: rows.steps.filter((step) => ACCOUNT_STEP_IDS.includes(step.id)) };
}

/** True when every row is `ok`. */
export function allStepsOk(rows: SetupRows): boolean {
  return rows.steps.every((step) => step.status === "ok");
}

/** Parse a report from the bridge; null when it does not parse. */
export function parseSetupReport(value: unknown): SetupReport | null {
  const parsed = setupReportSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** True when the app sends the ordered Setup report (epic PI-SPEC-SW-E288). */
export function hasSetupReport(bridge: ToolboxBridge | null): boolean {
  return typeof bridge?.app.getSetup === "function";
}

interface SetupReportQueryData {
  rows: SetupRows;
  source: SetupSource;
}

/**
 * The five Setup rows for this computer: `getSetup()` with `onSetupEvent`
 * updates, re-read every {@link SETUP_POLL_MS}; otherwise `checkEnvironment()`
 * mapped by {@link mapEnvironmentToSteps}. Disabled in a plain browser.
 */
export function useSetupReport(bridge: ToolboxBridge | null) {
  const queryClient = useQueryClient();
  const setupCapable = hasSetupReport(bridge);

  const query = useQuery({
    queryKey: setupKeys.report(),
    queryFn: async (): Promise<SetupReportQueryData> => {
      if (!bridge) throw new Error("SpecTree Toolbox is not available");
      if (typeof bridge.app.getSetup === "function") {
        return { rows: setupReportSchema.parse(await bridge.app.getSetup()), source: "setup" };
      }
      const environment = environmentReportSchema.parse(await bridge.app.checkEnvironment());
      return { rows: mapEnvironmentToSteps(environment), source: "environment" };
    },
    enabled: bridge !== null,
    retry: false,
    staleTime: 0,
    refetchInterval: SETUP_POLL_MS,
  });

  useEffect(() => {
    if (!bridge || !setupCapable || typeof bridge.app.onSetupEvent !== "function") return undefined;
    return bridge.app.onSetupEvent((event) => {
      const parsed = setupEventSchema.safeParse(event);
      if (!parsed.success) return;
      queryClient.setQueryData<SetupReportQueryData>(setupKeys.report(), {
        rows: parsed.data.report,
        source: "setup",
      });
    });
  }, [bridge, setupCapable, queryClient]);

  /** Put a report the app returned (from `runSetupStep`) into the list; one that does not parse is ignored. */
  const applyReport = (value: unknown): boolean => {
    const report = parseSetupReport(value);
    if (!report) return false;
    queryClient.setQueryData<SetupReportQueryData>(setupKeys.report(), { rows: report, source: "setup" });
    return true;
  };

  return { ...query, applyReport };
}
